<template>
  <a-modal :title="title" :width="1100" :visible="visible" :confirmLoading="confirmLoading" @ok="handleSubmit" @cancel="handleCancel">
    <a-spin :spinning="confirmLoading">
      <a-form-model ref="form" :model="form" :rules="rules" :label-col="{ span: 3 }" :wrapper-col="{ span: 20 }">
        <a-form-model-item label="调出门店" prop="fromOrgId">
          <a-select
            v-model="form.fromOrgId"
            placeholder="请选择调出门店"
            show-search
            :filter-option="filterOption"
            @change="handleFromOrgChange"
            style="width: 300px"
          >
            <a-select-option v-for="item in storeList" :key="item.id" :value="item.id">
              {{ item.shopName }}
            </a-select-option>
          </a-select>
        </a-form-model-item>

        <a-form-model-item label="调入门店" prop="toOrgId">
          <a-select
            v-model="form.toOrgId"
            placeholder="请选择调入门店"
            show-search
            :filter-option="filterOption"
            @change="handleToOrgChange"
            style="width: 300px"
          >
            <a-select-option v-for="item in storeList" :key="item.id" :value="item.id">
              {{ item.shopName }}
            </a-select-option>
          </a-select>
        </a-form-model-item>

        <a-form-model-item label="选择商品">
          <a-alert
            v-if="!form.fromOrgId"
            message="请先选择调出门店"
            type="warning"
            show-icon
            style="margin-bottom: 8px"
          />
          <a-select
            :disabled="!form.fromOrgId"
            :placeholder="form.fromOrgId ? '请选择调拨商品（支持搜索）' : '请先选择调出门店'"
            v-model="selectedSkuIds"
            mode="multiple"
            show-search
            :filter-option="false"
            :default-active-first-option="false"
            :not-found-content="notFoundContent"
            @search="handleGoodsSearch"
            @change="handleGoodsChange"
            @dropdownVisibleChange="handleDropdownVisibleChange"
            style="width: 100%"
          >
            <template v-if="goodsSearchLoading" slot="notFoundContent">
              <a-spin size="small" />
            </template>
            <template v-else slot="notFoundContent">
              <span>暂无数据</span>
            </template>
            <a-select-option v-for="item in goodsOptions" :key="item.skuId" :value="item.skuId">
              {{ item.goodsName }}
              <span v-if="item.skuName" style="color: #666; margin: 0 4px;">【{{ item.skuName }}】</span>
              <span v-if="item.goodsCode" style="color: #999">({{ item.goodsCode }})</span>
              <a-tag v-if="item.stock > 0" color="green" style="margin-left: 8px">库存: {{ item.stock }}</a-tag>
              <a-tag v-else color="red" style="margin-left: 8px">无库存</a-tag>
            </a-select-option>
          </a-select>
        </a-form-model-item>

        <a-form-model-item label="调拨备注" prop="remark">
          <a-textarea
            v-model="form.remark"
            placeholder="请输入备注（选填）"
            :rows="2"
            :maxLength="200"
            style="width: 600px"
          />
        </a-form-model-item>
      </a-form-model>

      <!-- 商品调拨数量设置区域 -->
      <div>
        <a-divider>商品调拨数量设置</a-divider>

        <!-- 统一设置区域 -->
        <a-form layout="inline" style="margin-bottom: 16px; background: #f5f5f5; padding: 12px; border-radius: 4px;">
          <a-form-item label="统一设置">
            <a-input-number
              v-model="uniformSettings.transferCount"
              :min="1"
              :precision="0"
              :max="999999"
              placeholder="调拨数量"
              style="width: 120px"
            />
            <a-button type="primary" @click="handleUniformSet" style="margin-left: 8px">
              <a-icon type="check" />统一设置
            </a-button>
            <a-button @click="handleResetAll" style="margin-left: 8px">
              <a-icon type="undo" />重置所有
            </a-button>
          </a-form-item>
        </a-form>

        <!-- 商品表格 -->
        <a-table
          :columns="goodsColumns"
          :data-source="selectedGoods"
          :pagination="false"
          size="small"
          :rowKey="'skuId'"
          :scroll="{ y: 400 }"
        >
          <span slot="goodsName" slot-scope="text, record">
            <img v-if="record.picture" :src="record.picture" style="width: 40px; height: 40px; margin-right: 8px; vertical-align: middle;" />
            {{ record.goodsName }}
            <a-tag v-if="record.skuName" color="orange" style="margin-left: 8px">{{ record.skuName }}</a-tag>
          </span>
          <span slot="availableStock" slot-scope="text">
            <a-tag :color="text > 0 ? 'green' : 'red'">{{ text || 0 }}</a-tag>
          </span>
          <span slot="stockStatus" slot-scope="text, record">
            <a-tag v-if="record.availableStock > 10" color="green">充足</a-tag>
            <a-tag v-else-if="record.availableStock > 0" color="orange">紧张</a-tag>
            <a-tag v-else color="red">无货</a-tag>
          </span>
          <span slot="transferCount" slot-scope="text, record">
            <a-input-number
              v-model="record.transferCount"
              :min="1"
              :precision="0"
              :max="record.availableStock || 999999"
              style="width: 120px"
              placeholder="调拨数量"
            />
          </span>
          <span slot="action" slot-scope="text, record">
            <a-button type="link" size="small" @click="removeGoods(record)">
              <a-icon type="delete" />移除
            </a-button>
          </span>
        </a-table>
      </div>
    </a-spin>
  </a-modal>
</template>

<script>
import { createTransferOrder } from '@/api/retail/transfer'
import { getShopList } from '@/api/shop/shop'
import { getStorePurchaseInventoryList } from '@/api/retail/inventory'
import { debounce } from 'lodash'

export default {
  name: 'CreateForm',
  data() {
    return {
      visible: false,
      confirmLoading: false,
      title: '创建调拨单',
      selectedSkuIds: [],
      selectedGoods: [],
      goodsOptions: [],
      goodsSearchLoading: false,
      notFoundContent: '暂无数据',
      storeList: [],
      form: {
        fromOrgId: undefined,
        toOrgId: undefined,
        remark: ''
      },
      rules: {
        fromOrgId: [{ required: true, message: '请选择调出门店', trigger: 'change' }],
        toOrgId: [{ required: true, message: '请选择调入门店', trigger: 'change' }]
      },
      goodsColumns: [
        {
          title: '商品信息',
          dataIndex: 'goodsName',
          key: 'goodsName',
          scopedSlots: { customRender: 'goodsName' },
          width: 300,
          fixed: 'left'
        },
        {
          title: '可用库存',
          dataIndex: 'availableStock',
          key: 'availableStock',
          scopedSlots: { customRender: 'availableStock' },
          width: 100,
          align: 'center'
        },
        {
          title: '库存状态',
          dataIndex: 'stockStatus',
          key: 'stockStatus',
          scopedSlots: { customRender: 'stockStatus' },
          width: 100,
          align: 'center'
        },
        {
          title: '调拨数量',
          dataIndex: 'transferCount',
          key: 'transferCount',
          scopedSlots: { customRender: 'transferCount' },
          width: 150,
          align: 'center'
        },
        {
          title: '操作',
          key: 'action',
          scopedSlots: { customRender: 'action' },
          width: 80,
          align: 'center',
          fixed: 'right'
        }
      ],
      uniformSettings: {
        transferCount: undefined
      }
    }
  },
  created() {
    this.handleGoodsSearch = debounce(this.searchGoods, 300)
  },
  methods: {
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
      )
    },
    handleFromOrgChange() {
      if (this.form.fromOrgId && this.form.toOrgId && this.form.fromOrgId === this.form.toOrgId) {
        this.$message.warning('调出门店和调入门店不能相同')
        this.form.toOrgId = undefined
      }
      // 调出门店变化时,重新加载该门店的商品和库存
      if (this.form.fromOrgId) {
        this.loadGoodsByStore()
      } else {
        this.goodsOptions = []
        this.selectedGoods = []
        this.selectedSkuIds = []
      }
    },
    handleToOrgChange() {
      if (this.form.fromOrgId && this.form.toOrgId && this.form.fromOrgId === this.form.toOrgId) {
        this.$message.warning('调出门店和调入门店不能相同')
        this.form.toOrgId = undefined
      }
    },
    handleDropdownVisibleChange(visible) {
      if (visible && !this.goodsSearchLoading) {
        if (!this.form.fromOrgId) {
          this.$message.warning('请先选择调出门店')
          return
        }
        if (this.goodsOptions.length === 0) {
          this.loadGoodsByStore()
        }
      }
    },
    handleAdd() {
      this.form.fromOrgId = undefined
      this.form.toOrgId = undefined
      this.form.remark = ''
      this.selectedSkuIds = []
      this.selectedGoods = []
      this.goodsOptions = []
      this.uniformSettings = {
        transferCount: undefined
      }
      // 加载门店列表
      this.loadStoreList()
      this.visible = true
    },
    loadStoreList() {
      getShopList({ shopType: 3 }).then(response => {
        this.storeList = response.data || []
      }).catch(() => {
        this.storeList = []
      })
    },
    loadGoodsByStore() {
      if (!this.form.fromOrgId) {
        this.$message.warning('请先选择调出门店')
        return
      }
      this.goodsSearchLoading = true
      getStorePurchaseInventoryList({
        storeId: this.form.fromOrgId,
        current: 1,
        size: 1000
      }).then(response => {
        const inventoryList = response.data?.records || response.data || []
        // 过滤出有库存的商品
        this.goodsOptions = inventoryList.filter(item => item.stock > 0)
        this.goodsSearchLoading = false
      }).catch(() => {
        this.goodsSearchLoading = false
      })
    },
    searchGoods(value) {
      if (!value || value.trim() === '') {
        // 如果搜索内容为空,重新加载该门店的商品和库存
        this.loadGoodsByStore()
        return
      }
      if (!this.form.fromOrgId) {
        this.$message.warning('请先选择调出门店')
        return
      }
      this.goodsSearchLoading = true
      getStorePurchaseInventoryList({
        storeId: this.form.fromOrgId,
        goodsName: value,
        current: 1,
        size: 1000
      }).then(response => {
        const inventoryList = response.data?.records || response.data || []
        // 过滤出有库存的商品并匹配搜索关键词
        this.goodsOptions = inventoryList.filter(item => {
          const hasStock = item.stock > 0
          const matchKeyword = !value || (item.goodsName && item.goodsName.toLowerCase().includes(value.toLowerCase())) ||
                               (item.goodsCode && item.goodsCode.toLowerCase().includes(value.toLowerCase()))
          return hasStock && matchKeyword
        })
        this.goodsSearchLoading = false
      }).catch(() => {
        this.goodsSearchLoading = false
      })
    },
    handleGoodsChange(value) {
      const existingSkuIds = this.selectedGoods.map(item => item.skuId)
      const newSkuIds = value.filter(id => !existingSkuIds.includes(id))

      if (newSkuIds.length > 0) {
        for (const skuId of newSkuIds) {
          const goodsOption = this.goodsOptions.find(item => item.skuId === skuId)
          if (goodsOption) {
            const exists = this.selectedGoods.find(item => item.skuId === skuId)
            if (!exists) {
              this.selectedGoods.push({
                id: goodsOption.skuId,
                goodsId: goodsOption.goodsId,
                skuId: goodsOption.skuId,
                goodsName: goodsOption.goodsName,
                skuName: goodsOption.skuName || '',
                picture: goodsOption.picture,
                availableStock: goodsOption.stock || 0,
                shopPrice: goodsOption.shopPrice || 0,
                salePrice: goodsOption.salePrice || 0,
                transferCount: 1
              })
            }
          }
        }
      }

      this.selectedGoods = this.selectedGoods.filter(item => {
        return value.includes(item.skuId)
      })
    },
    removeGoods(record) {
      const skuIndex = this.selectedSkuIds.indexOf(record.skuId)
      if (skuIndex > -1) {
        this.selectedSkuIds.splice(skuIndex, 1)
      }

      const index = this.selectedGoods.findIndex(item => item.id === record.id)
      if (index > -1) {
        this.selectedGoods.splice(index, 1)
      }
    },
    handleSubmit() {
      this.$refs.form.validate(valid => {
        if (!valid) {
          return false
        }

        if (this.selectedGoods.length === 0) {
          this.$message.warning('请选择商品')
          return
        }

        if (this.form.fromOrgId === this.form.toOrgId) {
          this.$message.warning('调出门店和调入门店不能相同')
          return
        }

        const invalidGoods = this.selectedGoods.find(item => {
          return item.transferCount === undefined || item.transferCount === null || item.transferCount <= 0
        })

        if (invalidGoods) {
          this.$message.warning('请完善所有商品的调拨数量')
          return
        }

        this.confirmLoading = true

        const items = this.selectedGoods.map(item => ({
          goodsId: item.goodsId,
          skuId: item.skuId,
          transferCount: item.transferCount,
          shopPrice: item.shopPrice || 0,
          salePrice: item.salePrice || 0
        }))

        const params = {
          fromOrgId: this.form.fromOrgId,
          fromOrgType: 3,
          toOrgId: this.form.toOrgId,
          toOrgType: 3,
          remark: this.form.remark,
          items: items
        }

        createTransferOrder(params).then(() => {
          this.$message.success('调拨单创建成功')
          this.visible = false
          this.$emit('ok')
        }).catch(error => {
          this.$message.error('调拨单创建失败：' + (error.message || '未知错误'))
        }).finally(() => {
          this.confirmLoading = false
        })
      })
    },
    handleCancel() {
      this.visible = false
      this.selectedSkuIds = []
      this.selectedGoods = []
      this.form.fromOrgId = undefined
      this.form.toOrgId = undefined
      this.form.remark = ''
      this.uniformSettings = {
        transferCount: undefined
      }
    },
    handleUniformSet() {
      if (!this.uniformSettings.transferCount || this.uniformSettings.transferCount <= 0) {
        this.$message.warning('请先设置要统一的调拨数量')
        return
      }

      this.selectedGoods.forEach(item => {
        item.transferCount = this.uniformSettings.transferCount
      })

      this.$message.success('统一设置成功')
    },
    handleResetAll() {
      this.$confirm({
        title: '确认重置',
        content: '确定要重置所有商品的调拨数量为1吗？',
        onOk: () => {
          this.selectedGoods.forEach(item => {
            item.transferCount = 1
          })
          this.uniformSettings = {
            transferCount: 1
          }
          this.$message.success('重置成功')
        }
      })
    }
  }
}
</script>

<style scoped>
:deep(.ant-table) {
  font-size: 13px;
}
</style>
